/**
 * k4 — <k4-countdown>: odliczanie w dół.
 *
 * Dwa tryby:
 *
 * 1. Wektor (data-k4-start + data-k4-vector="lata,miesiące,dni,godziny,minuty,sekundy"):
 *    licznik STARTUJE z literalnych wartości wektora w chwili `start` i odejmuje
 *    upływający czas pożyczając tylko w dół (sekundy → minuty → godziny → dni →
 *    miesiące → lata; miesiąc = 30 dni, rok = 12 miesięcy). Nigdy nie
 *    „normalizuje" w górę, więc 44 miesiące pokazują się jako 44, nie jako
 *    3 lata i 8. Tak chciał właściciel (2026-10-13): na starcie 44 · 44 · 44,
 *    a dalej czas leci normalnie.
 *
 * 2. Termin (data-k4-deadline, ISO 8601): klasyczne pozostało do daty,
 *    z latami tylko gdy jest komórka [data-k4-cd="years"] (rok = 365,25 dnia).
 *
 * Odświeżanie co sekundę tylko przy widocznej karcie; po powrocie przelicza
 * od zera. Po wyzerowaniu: stop i etykieta z data-k4-label-live na
 * sąsiednim [data-k4-cd-label]. Zły start/termin: zostają kreski z markupu.
 */
const BASES = { seconds: 60, minutes: 60, hours: 24, days: 30, months: 12 };
const ORDER = ['seconds', 'minutes', 'hours', 'days', 'months', 'years'];

class K4Countdown extends HTMLElement {
  connectedCallback() {
    this.cells = {};
    this.querySelectorAll('[data-k4-cd]').forEach((el) => {
      this.cells[el.dataset.k4Cd] = el;
    });
    this.label = this.parentElement && this.parentElement.querySelector('[data-k4-cd-label]');

    const vector = this.dataset.k4Vector;
    const start = this.dataset.k4Start ? Date.parse(this.dataset.k4Start) : NaN;
    const deadline = this.dataset.k4Deadline ? Date.parse(this.dataset.k4Deadline) : NaN;

    if (vector && !Number.isNaN(start)) {
      const parts = vector.split(',').map((n) => parseInt(n, 10) || 0);
      this.mode = 'vector';
      this.start = start;
      this.vector = { years: parts[0], months: parts[1], days: parts[2], hours: parts[3], minutes: parts[4], seconds: parts[5] };
    } else if (!Number.isNaN(deadline)) {
      this.mode = 'deadline';
      this.deadline = deadline;
    } else {
      return;
    }

    this.tick = this.tick.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.run();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  onVisibility() {
    if (document.hidden) this.stop();
    else this.run();
  }

  run() {
    this.tick();
    if (!this.timer) this.timer = setInterval(this.tick, 1000);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  tick() {
    const values = this.mode === 'vector' ? this.fromVector() : this.fromDeadline();
    if (!values) return;

    ORDER.forEach((unit) => {
      if (!this.cells[unit]) return;
      const pad = unit === 'seconds' || unit === 'minutes' || unit === 'hours';
      this.write(unit, values[unit], pad);
    });

    const done = ORDER.every((unit) => !values[unit]);
    if (done) {
      this.stop();
      if (this.label && this.label.dataset.k4LabelLive) this.label.textContent = this.label.dataset.k4LabelLive;
    }
  }

  /* Wektor minus upływ czasu, pożyczanie tylko w dół. */
  fromVector() {
    const elapsed = Math.max(0, Math.floor((Date.now() - this.start) / 1000));
    const v = { ...this.vector };
    v.seconds -= elapsed;

    for (let i = 0; i < ORDER.length - 1; i += 1) {
      const unit = ORDER[i];
      const upper = ORDER[i + 1];
      if (v[unit] < 0) {
        const borrow = Math.ceil(-v[unit] / BASES[unit]);
        v[unit] += borrow * BASES[unit];
        v[upper] -= borrow;
      }
    }

    if (v.years < 0) return { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    return v;
  }

  fromDeadline() {
    let left = Math.max(0, Math.floor((this.deadline - Date.now()) / 1000));
    let days = Math.floor(left / 86400);
    left -= days * 86400;
    let years = 0;
    if (this.cells.years) {
      years = Math.floor(days / 365.25);
      days = Math.floor(days - years * 365.25);
    }
    const hours = Math.floor(left / 3600);
    left -= hours * 3600;
    const minutes = Math.floor(left / 60);
    const seconds = left - minutes * 60;
    return { years, months: 0, days, hours, minutes, seconds };
  }

  write(unit, value, pad) {
    const el = this.cells[unit];
    const text = pad ? String(value).padStart(2, '0') : String(value);
    if (el.textContent !== text) el.textContent = text;
  }
}

if (!customElements.get('k4-countdown')) customElements.define('k4-countdown', K4Countdown);
